import { ANNOTATION_VALUE } from "@/constants/annotation-values"

import {
  FLOAT_COMPARISON_OPERATOR,
  RANGE_COMPARISON_OPERATOR,
  SET_COMPARISON_OPERATOR,
  type TargetCondition,
} from "@/types/metrics/contracts"

import { deriveYesNoString } from "./binaryValue"
import {
  getMetricType,
  isValidStringMetadataTargetCondition,
  METRIC_TYPE,
  resolveBinaryTargetCondition,
  resolveSuccessValue,
  resolveThreshold,
  type MetricThresholdResolutionSource,
  type MetricTypeSource,
  type ResolvedThreshold,
} from "./resolveThreshold"

/**
 * Pass/fail color for a metric value: "success" (green), "danger" (red), or
 * "default" (gray/neutral). Used as a key into the shared yes/no badge class maps.
 */
type MetricColorValue = "default" | "success" | "danger"

/**
 * Determines the color for a YES/NO/UNKNOWN metric value based on target condition.
 *
 * @param value - The metric value ("YES", "NO", or "UNKNOWN")
 * @param targetCondition - Optional target condition with target_values and comparison_operator
 * @returns Color string: "success" (green), "danger" (red), or "default" (gray)
 */
const getStringMetricColor = (
  value: string,
  targetCondition?: TargetCondition,
): MetricColorValue => {
  // UNKNOWN or invalid values are always default
  if (
    value === ANNOTATION_VALUE.UNKNOWN ||
    (value !== ANNOTATION_VALUE.YES && value !== ANNOTATION_VALUE.NO)
  ) {
    return "default"
  }

  // No target condition => use default colors for values
  if (!targetCondition || !("target_values" in targetCondition)) {
    switch (value) {
      case ANNOTATION_VALUE.YES:
        return "success"
      case ANNOTATION_VALUE.NO:
        return "danger"
      default:
        return "default"
    }
  }

  const { target_values, comparison_operator } = targetCondition
  const valueInTarget = target_values.includes(value)

  // "in" => valueInTarget is ideal (success), "nin" => !valueInTarget is ideal (success)
  const isIdeal =
    comparison_operator === SET_COMPARISON_OPERATOR.IN
      ? valueInTarget
      : !valueInTarget

  return isIdeal ? "success" : "danger"
}

const getNumericMetricColor = (
  value: number,
  targetCondition?: TargetCondition,
  floatToBool = false,
): MetricColorValue => {
  if (!Number.isFinite(value) || (floatToBool && value !== 0 && value !== 1)) {
    return "default"
  }

  if (!targetCondition || !("target_float" in targetCondition)) {
    if (floatToBool) {
      switch (value) {
        case 1:
          return "success"
        case 0:
          return "danger"
        default:
          return "default"
      }
    } else {
      return "default"
    }
  }

  const { target_float, comparison_operator } = targetCondition

  switch (comparison_operator) {
    case FLOAT_COMPARISON_OPERATOR.GT:
      return value > target_float ? "success" : "danger"
    case FLOAT_COMPARISON_OPERATOR.GTE:
      return value >= target_float ? "success" : "danger"
    case FLOAT_COMPARISON_OPERATOR.LT:
      return value < target_float ? "success" : "danger"
    case FLOAT_COMPARISON_OPERATOR.LTE:
      return value <= target_float ? "success" : "danger"
    case FLOAT_COMPARISON_OPERATOR.EQ:
      return value === target_float ? "success" : "danger"
    case FLOAT_COMPARISON_OPERATOR.NEQ:
      return value !== target_float ? "success" : "danger"
    default:
      return "default"
  }
}

export const getMetricColor = (
  value: number | string | null | undefined,
  targetCondition?: TargetCondition,
  floatToBool = false,
): MetricColorValue => {
  if (value == null) {
    return "default"
  }
  if (typeof value === "number") {
    return getNumericMetricColor(value, targetCondition, floatToBool)
  }
  return getStringMetricColor(value, targetCondition)
}

export const getMetricColorClass = (color: MetricColorValue): string =>
  color === "success"
    ? "bg-coval-success-surface font-medium text-coval-success-on-surface"
    : color === "danger"
      ? "bg-coval-danger-surface font-medium text-coval-danger-on-surface"
      : ""

export const getMetricTextColorClass = (color: MetricColorValue): string =>
  color === "danger"
    ? "text-coval-danger-text"
    : color === "success"
      ? "text-coval-success-text"
      : "text-coval-text"

/**
 * Determine pass/fail color from a resolved threshold.
 * Handles numeric range operators (bt/nbt) and categorical membership.
 */
const getMetricColorFromThreshold = (
  value: number | string,
  threshold: ResolvedThreshold,
  metricType: ReturnType<typeof getMetricType>,
): MetricColorValue => {
  const { comparison_operator } = threshold

  if (metricType === METRIC_TYPE.NUMERICAL && typeof value === "number") {
    const upper = threshold.target_float_upper ?? 0
    const lower = threshold.target_float_lower ?? upper

    switch (comparison_operator) {
      case FLOAT_COMPARISON_OPERATOR.GT:
        return value > upper ? "success" : "danger"
      case FLOAT_COMPARISON_OPERATOR.GTE:
        return value >= upper ? "success" : "danger"
      case FLOAT_COMPARISON_OPERATOR.LT:
        return value < upper ? "success" : "danger"
      case FLOAT_COMPARISON_OPERATOR.LTE:
        return value <= upper ? "success" : "danger"
      case FLOAT_COMPARISON_OPERATOR.EQ:
        return value === upper ? "success" : "danger"
      case FLOAT_COMPARISON_OPERATOR.NEQ:
        return value !== upper ? "success" : "danger"
      case RANGE_COMPARISON_OPERATOR.BT:
        return value >= lower && value <= upper ? "success" : "danger"
      case RANGE_COMPARISON_OPERATOR.NBT:
        return value < lower || value > upper ? "success" : "danger"
      case "in":
      case "nin":
      default:
        return "default"
    }
  }

  if (
    (metricType === METRIC_TYPE.CATEGORICAL ||
      metricType === METRIC_TYPE.SET) &&
    typeof value === "string"
  ) {
    const targetValues = threshold.target_values ?? []
    const isIdeal =
      comparison_operator === SET_COMPARISON_OPERATOR.IN
        ? targetValues.includes(value)
        : !targetValues.includes(value)
    return isIdeal ? "success" : "danger"
  }

  return "default"
}

/**
 * Colors one metric row. Numeric binary organization targets apply only to aggregate rates.
 */
export const getMetricColorForMetric = (
  value: number | string | null | undefined,
  metric: MetricTypeSource & MetricThresholdResolutionSource,
  floatToBool = false,
): MetricColorValue => {
  if (value == null || (typeof value === "number" && !Number.isFinite(value))) {
    return "default"
  }
  const metricType = getMetricType(metric)
  if (metricType === METRIC_TYPE.BINARY) {
    const normalized = deriveYesNoString(
      typeof value === "number"
        ? { float_value: value }
        : { string_value: value },
    )
    return getStringMetricColor(
      normalized,
      resolveBinaryTargetCondition(metric),
    )
  }
  const threshold = resolveThreshold(metric)
  if (!threshold) {
    const targetCondition =
      metric.metric_manager === "manager_metadata_field_metric" &&
      metric.metric_metadata?.metadata_field_type === "STRING" &&
      !isValidStringMetadataTargetCondition(
        metric.metric_metadata?.target_condition,
      )
        ? undefined
        : metric.metric_metadata?.target_condition
    return getMetricColor(value, targetCondition, floatToBool)
  }
  return getMetricColorFromThreshold(value, threshold, metricType)
}

/**
 * Evaluate the configured passing-answer rate. Convert the stored raw YES-average
 * target to that rate's polarity; the supplied pass rate is already normalized.
 */
export const getBinaryAggregateColor = (
  passRate: number | null,
  metric: MetricThresholdResolutionSource,
): MetricColorValue => {
  const threshold = resolveThreshold(metric)
  if (
    passRate === null ||
    threshold?.source !== "organization_threshold" ||
    threshold.target_float_upper == null
  ) {
    return "default"
  }
  // Stored binary targets compare the raw YES average, including for configured NO-pass metrics.
  const isInverted = resolveSuccessValue(metric) === ANNOTATION_VALUE.NO
  if (!isInverted) {
    return getMetricColorFromThreshold(
      passRate,
      threshold,
      METRIC_TYPE.NUMERICAL,
    )
  }
  const inverseOperators = {
    gt: FLOAT_COMPARISON_OPERATOR.LT,
    gte: FLOAT_COMPARISON_OPERATOR.LTE,
    lt: FLOAT_COMPARISON_OPERATOR.GT,
    lte: FLOAT_COMPARISON_OPERATOR.GTE,
  } as const
  const op = threshold.comparison_operator
  if (op !== "gt" && op !== "gte" && op !== "lt" && op !== "lte") {
    return "default"
  }
  // ThresholdEditor stores binary targets to six decimal places.
  const binaryTargetPrecision = 1e6
  const successTarget =
    Math.round((1 - threshold.target_float_upper) * binaryTargetPrecision) /
    binaryTargetPrecision
  // Complementing a YES average can leave a rounding error at an exact boundary.
  const comparisonRate =
    Math.abs(passRate - successTarget) <= Number.EPSILON
      ? successTarget
      : passRate
  return getMetricColorFromThreshold(
    comparisonRate,
    {
      ...threshold,
      comparison_operator: inverseOperators[op],
      target_float_upper: successTarget,
    },
    METRIC_TYPE.NUMERICAL,
  )
}
